// consent.ts — records consent decisions to Supabase (the demonstrable, auditable
// log). It drives nothing in the UI itself; the local progress flags do that. Fail-soft:
// a write that can't reach the network is queued on-device and flushed on the next
// sign-in / foreground, so a decision made offline (e.g. withdrawing on a train) is
// never lost. The row's user_id is filled server-side from auth.uid() (column default
// + RLS), so we never send identity here.
import { supabase } from './supabaseClient';
import { storage } from './storage';

export type ConsentKind = 'terms' | 'research';
export type ConsentAction = 'accepted' | 'granted' | 'declined' | 'withdrawn';

interface ConsentEvent {
  kind: ConsentKind;
  action: ConsentAction;
  doc_version: string;
  created_at: string;
}

const QUEUE_KEY = 'renurture.consent.queue.v1';
const loadQueue = (): ConsentEvent[] => storage.getJSON<ConsentEvent[]>(QUEUE_KEY, []);
const saveQueue = (q: ConsentEvent[]): void => storage.setJSON(QUEUE_KEY, q.slice(-50));

async function insert(ev: ConsentEvent): Promise<boolean> {
  const sb = supabase();
  if (!sb) return false;
  try {
    // created_at carries the event's REAL time (so a queued offline event flushed
    // later is logged at when it happened, not when it synced).
    const { error } = await sb.from('consents').insert({
      kind: ev.kind,
      action: ev.action,
      doc_version: ev.doc_version,
      created_at: ev.created_at,
    });
    return !error;
  } catch {
    return false;
  }
}

/** The user's CURRENT decision for one kind: the latest row in the append-only log. */
export interface LatestConsent {
  action: ConsentAction;
  doc_version: string;
  created_at: string;
}

/**
 * Read back what this user already decided, so a reinstall or a new device is not
 * mistaken for a new user.
 *
 * The decision has always been recorded here (logConsent writes it, RLS pins it to
 * auth.uid(), and there is a select-own policy) — but nothing ever read it, while the
 * app's own copy lived only in device storage. The result was that someone who
 * consented months ago, then reinstalled, was marched back through the consent gate
 * with their answer sitting in the database the whole time.
 *
 * The log is append-only and a withdrawal is a NEW row, so the newest row per kind IS
 * the current position. Returns null for a kind with no history, and null overall on
 * any failure — the caller then falls back to asking, which is the safe direction.
 */
export async function fetchLatestConsents(): Promise<{ terms: LatestConsent | null; research: LatestConsent | null } | null> {
  const sb = supabase();
  if (!sb) return null;
  try {
    const { data, error } = await sb
      .from('consents')
      .select('kind, action, doc_version, created_at')
      .order('created_at', { ascending: false });
    if (error || !data) return null;
    const first = (kind: ConsentKind): LatestConsent | null => {
      const row = data.find((r: { kind: string }) => r.kind === kind);
      return row ? { action: row.action, doc_version: row.doc_version, created_at: row.created_at } : null;
    };
    return { terms: first('terms'), research: first('research') };
  } catch {
    return null;
  }
}

/** Record one consent decision. Best-effort now; queued + retried if offline. */
export async function logConsent(kind: ConsentKind, action: ConsentAction, docVersion: string): Promise<void> {
  const ev: ConsentEvent = { kind, action, doc_version: docVersion, created_at: new Date().toISOString() };
  if (!(await insert(ev))) saveQueue([...loadQueue(), ev]);
}

/** Flush any consent events captured while offline. Called on sign-in / foreground. */
export async function flushConsentQueue(): Promise<void> {
  const pending = loadQueue();
  if (!pending.length) return;
  saveQueue([]); // optimistically clear; failures are re-added below
  const stillFailing: ConsentEvent[] = [];
  for (const ev of pending) {
    if (!(await insert(ev))) stillFailing.push(ev);
  }
  if (stillFailing.length) saveQueue([...loadQueue(), ...stillFailing]);
}

/** Drop any queued consent events. Called when a DIFFERENT user signs in on this
 *  device, so the previous user's unsynced decision is never logged under the new
 *  account (it would be attributed to the wrong auth.uid()). */
export function clearConsentQueue(): void {
  storage.remove(QUEUE_KEY);
}
